
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { ActionsSubject } from '@ngrx/store';
import { ActivatedRouterStateSnapshot, B2BUser, createFrom, EventService, PageType, User } from '@spartacus/core';
import { UserAccountFacade } from "@spartacus/user/account/root";
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from "../../../../../../environments/environment";
import {
  CustomNavigationEvent,
  GeneralUserNavigatorEvent,
  USER_NAVIGATION_CLIENT_TYPE,
  USER_NAVIGATION_ENVIRONMENT,
  USER_NAVIGATION_LOGIN_STATUS
} from './custom-navigation.event';


/**
 * A builder for the NavigationEvent
 */
@Injectable({
  providedIn: 'root',
})
export class CustomNavigationEventBuilder {
  private GA_PREFIX = 6
  private GA_COOKIE_NAME = '_ga'

  constructor(
    protected actions: ActionsSubject,
    protected eventService: EventService,
    protected userAccountFacade: UserAccountFacade,
    @Inject(DOCUMENT) protected document: any,
  ) {
    this.register();
  }

  /**
   * Registers events
   */
  protected register(): void {
    this.eventService.register(GeneralUserNavigatorEvent, this.buildUserNavigationEvent());
    this.eventService.register(CustomNavigationEvent, this.buildNavigationEvent());
  }

  /**
   * Builds the navigation events
   */
  protected buildNavigationEvent(): Observable<CustomNavigationEvent> {
    return this.getNavigatedEvent().pipe(
      map((state) =>
        createFrom(CustomNavigationEvent, {
          event: 'navigation',
          context: state.context,
          semanticRoute: state.semanticRoute,
          url: state.url,
          params: state.params,
        }),
      ),
    );
  }

  /**
   * Builds the user navigation events
   */
  protected buildUserNavigationEvent(): Observable<GeneralUserNavigatorEvent> {
    return this.getNavigatedEvent().pipe(
      switchMap(state => {
          return this.userAccountFacade.get().pipe(
            map((user: User | B2BUser) => {
                const contentInfo = state.url.split('/')
                return createFrom(GeneralUserNavigatorEvent, {
                  login_status: user ? USER_NAVIGATION_LOGIN_STATUS.LOGIN : USER_NAVIGATION_LOGIN_STATUS.NO_LOGIN,
                  user_id: user?.uid ?? '',
                  client_type: user?.professional ? USER_NAVIGATION_CLIENT_TYPE.EMPRESA : USER_NAVIGATION_CLIENT_TYPE.PARTICULAR,
                  client_id: this.getCookie(document?.cookie, this.GA_COOKIE_NAME)?.slice(this.GA_PREFIX),
                  content_group: this.getContentGroup(state),
                  content_detail: contentInfo[2] ?? '',
                  environment: environment?.production ? USER_NAVIGATION_ENVIRONMENT.PRODUCTION : USER_NAVIGATION_ENVIRONMENT.PRE_PRODUCTION,
                })
              }
            )
          )
        }
      )
    );
  }

  private getNavigatedEvent(): Observable<ActivatedRouterStateSnapshot> {
    return this.actions.pipe(
      ofType<RouterNavigatedAction<ActivatedRouterStateSnapshot>>(
        ROUTER_NAVIGATED,
      ),
      map((event) => event.payload.routerState),
    );
  }

  private getContentGroup(state: ActivatedRouterStateSnapshot) {
    const pathSegments = state.url.split('/');
    if (this.isHomepage(state)) {
      return 'home'
    }

    let result = '';
    const urlMappings = {
      'category': 'familia',
      'c': 'categoría',
      'p': 'producto',
      'checkout': 'checkout',
      'cart': 'carrito',
      'mi-cuenta': 'mi_cuenta'
    };

    for (const [key, value] of Object.entries(urlMappings)) {
      if (pathSegments.includes(key)) {
        result = urlMappings[key]
        return value;
      }
    }

    return result && result.length > 0 ? result : pathSegments[1].replace(/\?.*$/, '');
  }

  private isHomepage(state: ActivatedRouterStateSnapshot) {
    const semanticRoutes: string[] = ['ohgar', 'ohbar', 'ohlet', 'outlet', 'home', ''];
    const pathSegments: string[] = state.context.id.split('/');
    const lastPathSegment: string = pathSegments[pathSegments.length - 1];

    return state.context.type === PageType.CONTENT_PAGE && semanticRoutes.includes(lastPathSegment);
  }

  private getCookie(cookie: string, name: string) {
    if (!cookie || cookie.length <= 0) return;
    const regExp = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)', 'g');
    const result: RegExpExecArray | null = regExp.exec(cookie);

    return (result && decodeURIComponent(result[1])) || '';
  }
}
